import React from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../../components/Input';
import Dropdown from '../../../../../components/Dropdown';
import Switch from '../../../../../components/Switch';
import TextArea from '../../../../../components/TextArea';
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import { TableContextProvider, Validation } from '../../../../../interfaces/common';
import { AddEdit as AddEditInterface } from '../../../../../interfaces/property.interface';
import Address from './Address';
import MaintenanceDetails from './MaintenanceDetails';

interface PropertyDetailsTabProps {
  formData: AddEditInterface;
  validation: Validation;
  values: TableContextProvider
  view?: boolean;
  managers: any[];
  allManagers: any[];
  customers: any[];
  allCustomers: any[];
  countries: any[];
  states: any[];
  allCountries: any[];
  allStates: any[];
  allCities: any[];
  cities: any[];
  distanceFromLocationOptions: any[];
  bookingTypes: any[];
  allBookingTypes: any[];
  allWhoCollectsMoneyOptions: any[];
  cleaningManagedByOptions: any[]
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any, anme?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
}

const PropertyDetailsTab: React.FC<PropertyDetailsTabProps> = ({
  formData,
  validation,
  values,
  view,
  managers,
  allManagers,
  customers,
  allCustomers,
  countries,
  states,
  allCountries,
  allStates,
  allCities,
  cities,
  distanceFromLocationOptions,
  bookingTypes,
  allBookingTypes,
  allWhoCollectsMoneyOptions,
  cleaningManagedByOptions,
  dropdownSearchData,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange
}) => {
  const { t } = useTranslation();

  return (
    <form className="card-body" autoComplete="off">
      <div className="row g-6">
        <h6 className="mb-0">
          1. {t('property.addEdit.pageTexts.form.sections.primaryDetails')}
        </h6>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.customer ? [formData.customer] : []}
              options={(customers || []).map((customer: any) => ({ 
                label: customer.name, 
                value: customer.id 
              }))}
              allOptions={(allCustomers || []).map((customer: any) => ({ 
                label: customer.name, 
                value: customer.id 
              }))}
              id={VALIDATION_FIELD_MAPPING.customer}
              name={VALIDATION_FIELD_MAPPING.customer}
              disabled={view}
              searchText={dropdownSearchData.customer}
              onSearch={onDropdownSearchChange}
              placeholder={t('property.addEdit.pageTexts.form.customer.placeholder')}
              label={t('property.addEdit.pageTexts.form.customer.label')}
              required
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.customer)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.customer)?.message ?? ''}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.hospitableId}
              name={VALIDATION_FIELD_MAPPING.hospitableId}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.hospitableId)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.hospitableId)?.message ?? ''}
              label={t('property.addEdit.pageTexts.form.hospitableId.label')}
              placeholder={t('property.addEdit.pageTexts.form.hospitableId.placeholder')}
              value={formData.hospitableId}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.hospitableId)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.hospitableId)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.name}
              name={VALIDATION_FIELD_MAPPING.name}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.name)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.name)?.message ?? ''}
              label={t('property.addEdit.pageTexts.form.name.label')}
              placeholder={t('property.addEdit.pageTexts.form.name.placeholder')}
              value={formData.name}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.name)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.name)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.publicName}
              name={VALIDATION_FIELD_MAPPING.publicName}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.publicName)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.publicName)?.message ?? ''}
              label={t('property.addEdit.pageTexts.form.publicName.label')}
              placeholder={t('property.addEdit.pageTexts.form.publicName.placeholder')}
              value={formData.publicName}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.publicName)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.publicName)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.pictureUrl}
              name={VALIDATION_FIELD_MAPPING.pictureUrl}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.pictureUrl)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.pictureUrl)?.message ?? ''}
              label={t('property.addEdit.pageTexts.form.pictureUrl.label')}
              placeholder={t('property.addEdit.pageTexts.form.pictureUrl.placeholder')}
              value={formData.pictureUrl}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.pictureUrl)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.pictureUrl)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">
          2. {t('property.addEdit.pageTexts.form.sections.addressDetails')}
        </h6>
        <Address
          countries={countries}
          states={states}
          allCountries={allCountries}
          allStates={allStates}
          allCities={allCities}
          cities={cities}
          distanceFromLocationOptions={distanceFromLocationOptions}
          formData={formData}
          validation={validation}
          view={view}
          dropdownSearchData={dropdownSearchData}
          onDropdownSearchChange={onDropdownSearchChange}
          onKeyDown={onKeyDown}
          onInputBlur={onInputBlur}
          onInputChange={onInputChange}
        />
      </div>

      <hr className="my-6 mx-n4" />

      <MaintenanceDetails 
        formData={formData}
        validation={validation}
        values={values}
        view={view}
        managers={managers}
        allManagers={allManagers}
        bookingTypes={bookingTypes}
        allBookingTypes={allBookingTypes}
        cleaningManagedByOptions={cleaningManagedByOptions}
        allWhoCollectsMoneyOptions={allWhoCollectsMoneyOptions}
        dropdownSearchData={dropdownSearchData}
        error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.maintenanceDetails)}
        errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.maintenanceDetails)?.message ?? ''}
        onDropdownSearchChange={onDropdownSearchChange}
        onKeyDown={onKeyDown}
        onInputBlur={onInputBlur}
        onInputChange={onInputChange}
      />

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">
          4. {t('property.addEdit.pageTexts.form.sections.otherInformation')}
        </h6>

        <div className="col-md-9">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.notes}
              name={VALIDATION_FIELD_MAPPING.notes}
              disabled={view}
              label={t('property.addEdit.pageTexts.form.notes.label')}
              placeholder={t('property.addEdit.pageTexts.form.notes.placeholder')}
              value={formData.notes}
              rows={4}
              className="form-control-lg h-100"
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)?.message ?? ''}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.notes)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.notes)}
            />
          </div>
        </div>
        <div className="col-md-3 d-flex align-items-start">
          <div className="form-floating form-floating-outline">
            <Switch
              label={formData.status ? t('property.addEdit.pageTexts.form.status.active') : t('property.addEdit.pageTexts.form.status.inActive')}
              checked={formData.status}
              name={VALIDATION_FIELD_MAPPING.status}
              disabled={view}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.status)}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default PropertyDetailsTab;
